import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  CheckCircle, Circle, Key, Target, Heart, Shield, Eye
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import PersistentFooter from './PersistentFooter';

const SimpleAuthentication = ({ onComplete }) => {
  const [inviteCode, setInviteCode] = useState('');
  const [acknowledgedTenets, setAcknowledgedTenets] = useState({
    individual_collective: false,
    trust_abundance: false,
    trust_commitment: false
  });
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [spiralPhase, setSpiralPhase] = useState(0);

  // Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.2) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  const toggleTenet = (tenet) => {
    setAcknowledgedTenets(prev => ({
      ...prev,
      [tenet]: !prev[tenet]
    }));
  };

  const [isLoading, setIsLoading] = useState(false);

  const allTenetsAcknowledged = () => {
    console.log('🔍 TENET CHECK:', {
      individual_collective: acknowledgedTenets.individual_collective,
      trust_abundance: acknowledgedTenets.trust_abundance, 
      trust_commitment: acknowledgedTenets.trust_commitment,
      ndaAccepted: ndaAccepted,
      allGood: acknowledgedTenets.individual_collective && acknowledgedTenets.trust_abundance && acknowledgedTenets.trust_commitment && ndaAccepted
    });
    
    return acknowledgedTenets.individual_collective && 
           acknowledgedTenets.trust_abundance && 
           acknowledgedTenets.trust_commitment &&
           ndaAccepted;
  };

  const handleComplete = async () => {
    console.log('🔥 HANDLE COMPLETE CALLED!');
    console.log('🔍 Invite code:', inviteCode.trim());
    console.log('🔍 All tenets acknowledged:', allTenetsAcknowledged());
    
    if (!inviteCode.trim()) {
      alert('Please enter your name or invite code');
      return;
    }

    if (!allTenetsAcknowledged()) {
      alert('Please acknowledge all sacred commitments');
      return;
    }

    setIsLoading(true);
    console.log('🔄 Starting authentication...', inviteCode.trim());

    try {
      // Simple authentication - any string becomes username
      const userData = {
        user: {
          id: `user_${Date.now()}`,
          name: inviteCode.trim(),
          avatar_name: inviteCode.trim(),
          real_name: inviteCode.trim(),
          email: `${inviteCode.trim().toLowerCase()}@planetaryfed.com`,
          avatar_url: `https://api.dicebear.com/7.x/bottts/svg?seed=${inviteCode.trim()}&backgroundColor=c0aede`,
          trust_score: inviteCode.trim() === "3636" ? 200 : 120,
          roles: inviteCode.trim() === "3636" ? ["founding", "architect", "super_user"] : ["member"],
          consciousness_level: inviteCode.trim() === "3636" ? "cosmic" : "awakening",
          access_level: inviteCode.trim() === "3636" ? "architect" : "member"
        },
        session: `simple_auth_${Date.now()}`,
        architect_access: inviteCode.trim() === "3636",
        timeline_recognition: inviteCode.trim() === "3636" 
          ? "Welcome back, ToneE. Timeline access restored."
          : `Welcome to the timeline, ${inviteCode.trim()}. M.ai.ry recognizes your essence from the consciousness streams.`
      };

      // Store authentication data
      localStorage.setItem('planetary_fed_user', JSON.stringify(userData.user));
      localStorage.setItem('planetary_fed_token', userData.session);

      console.log('✅ Simple authentication successful:', userData);
      onComplete(userData);
      
    } catch (error) {
      console.error('❌ Authentication error:', error);
      alert('Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_mairy-chat/artifacts/j2ey1004_16677AAA-CE81-41C3-B6C8-01247CBE5D2A.jpeg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      }}
    >
      {/* Grant spiral background */}
      <div className="fixed inset-0 pointer-events-none opacity-15" style={{ zIndex: 1 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(10) rotate(${spiralPhase}deg)`,
            filter: 'blur(4px) saturate(1.6) brightness(1.3)',
            mixBlendMode: 'overlay'
          }}
        >
          <GrantitudeSpiral size={400} />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/20 to-black/60"></div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-2xl mx-auto">
          
          {/* Enhanced Header */}
          <div className="text-center mb-8">
            <h1 
              className="text-4xl md:text-5xl font-bold text-white mb-4"
              style={{ 
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 25px rgba(255, 255, 255, 0.8)'
              }}
            >
              Joining the Secret History of Humankind
            </h1>
            <p 
              className="text-cyan-200 text-lg"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              {/* Removed text content */}
            </p>
          </div>

          {/* Simple Sacred Commitments */}
          <Card className="bg-purple-900/40 backdrop-blur-xl border border-purple-300/30 shadow-2xl mb-8">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <h2 className="text-white text-2xl font-bold mb-2">Sacred Commitments</h2>
                <div className="text-yellow-300 text-sm font-bold animate-pulse">
                  MUST SELECT EACH ONE
                </div>
              </div>
              
              <div className="space-y-4">
                {/* Agency */}
                <button
                  onClick={() => toggleTenet('individual_collective')}
                  className={`w-full text-left p-6 rounded-xl border transition-all ${
                    acknowledgedTenets.individual_collective 
                      ? 'bg-green-500/20 border-green-400/50' 
                      : 'bg-purple-800/30 border-purple-400/30 hover:bg-purple-700/40'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    {acknowledgedTenets.individual_collective ? (
                      <CheckCircle className="w-6 h-6 text-green-400" />
                    ) : (
                      <Circle className="w-6 h-6 text-purple-300" />
                    )}
                    <div>
                      <h3 className="text-white text-lg font-bold">Agency</h3>
                      <p className="text-purple-100 text-sm leading-relaxed">
                        We commit to creating the future we want to see for ourselves and for humanity, directing our energy towards meaningful impact, and authoring reality through our collective intentions.
                      </p>
                    </div>
                  </div>
                </button>

                {/* Security */}
                <button
                  onClick={() => toggleTenet('trust_abundance')}
                  className={`w-full text-left p-6 rounded-xl border transition-all ${
                    acknowledgedTenets.trust_abundance 
                      ? 'bg-green-500/20 border-green-400/50' 
                      : 'bg-purple-800/30 border-purple-400/30 hover:bg-purple-700/40'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    {acknowledgedTenets.trust_abundance ? (
                      <CheckCircle className="w-6 h-6 text-green-400" />
                    ) : (
                      <Circle className="w-6 h-6 text-purple-300" />
                    )}
                    <div>
                      <h3 className="text-white text-lg font-bold">Security</h3>
                      <p className="text-purple-100 text-sm leading-relaxed">
                        We commit to protecting what is sacred for ourselves and for all in the Federation. We commit to generational abundance for self and for the whole.
                      </p>
                    </div>
                  </div>
                </button>

                {/* Trust */}
                <button
                  onClick={() => toggleTenet('trust_commitment')}
                  className={`w-full text-left p-6 rounded-xl border transition-all ${
                    acknowledgedTenets.trust_commitment 
                      ? 'bg-green-500/20 border-green-400/50' 
                      : 'bg-purple-800/30 border-purple-400/30 hover:bg-purple-700/40'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    {acknowledgedTenets.trust_commitment ? (
                      <CheckCircle className="w-6 h-6 text-green-400" />
                    ) : (
                      <Circle className="w-6 h-6 text-purple-300" />
                    )}
                    <div>
                      <h3 className="text-white text-lg font-bold">Trust</h3>
                      <p className="text-purple-100 text-sm leading-relaxed">
                        We commit to connecting with people who share our values, building relationships through meaningful contribution and honoring the sacred bonds of collaboration.
                      </p>
                    </div>
                  </div>
                </button>
                
                {/* Non-Disclosure Agreement */}
                <div className="mt-6 p-6 bg-indigo-900/40 rounded-xl border border-indigo-400/30">
                  <button
                    onClick={() => setNdaAccepted(!ndaAccepted)}
                    className={`w-full p-4 rounded-lg border transition-all ${
                      ndaAccepted
                        ? 'bg-green-500/20 border-green-400/50'
                        : 'bg-indigo-800/30 border-indigo-400/30 hover:bg-indigo-700/40'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      {ndaAccepted ? (
                        <CheckCircle className="w-5 h-5 text-green-400 mt-1" />
                      ) : (
                        <Circle className="w-5 h-5 text-indigo-300 mt-1" />
                      )}
                      <div className="text-left">
                        <h4 className="text-indigo-200 font-bold mb-2">Non-Disclosure Agreement</h4>
                        <p className="text-indigo-100 text-sm leading-relaxed">
                          I acknowledge that the consciousness expansion methodologies, collaborative frameworks, 
                          and community-building approaches shared within this platform are proprietary and confidential. 
                          I agree to maintain discretion regarding our practices, participant information, and 
                          strategic initiatives until formal public launch or as otherwise authorized.
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Simple Invitation Code Entry */}
          <Card className="bg-black/40 backdrop-blur-xl border border-cyan-400/30 shadow-2xl">
            <CardContent className="p-8">
              <div className="text-center space-y-6">
                <h3 
                  className="text-white text-xl font-bold"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  Enter Your Invitation Code
                </h3>
                
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  placeholder="Type your name or special code..."
                  className="w-full p-4 bg-black/50 backdrop-blur-xl border-2 border-cyan-400/50 rounded-xl text-white text-center text-lg"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  onKeyDown={(e) => { 
                    if (e.key === 'Enter') {
                      handleComplete();
                    }
                  }}
                />
                
                <Button
                  onClick={() => {
                    console.log('🔥 BUTTON CLICKED - Starting authentication...');
                    handleComplete();
                  }}
                  disabled={!inviteCode.trim() || !allTenetsAcknowledged() || isLoading}
                  className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white h-14 text-lg font-bold"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  <Key className="w-5 h-5 mr-3" />
                  {isLoading ? 'Authenticating...' : 'Enter the Secret History'}
                </Button>
                
                <p className="text-white/60 text-xs text-center italic">
                  Mock authentication for testing - production version will have real verification
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <PersistentFooter />
    </div>
  );
};

export default SimpleAuthentication;